import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d75n2o54hjod66j2ec7g';

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setStatus('sending');
    try {
      const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="w-full bg-[#f5f0ea] pt-36 pb-16 px-6 md:px-16">
        <p className="text-xs uppercase tracking-[0.3em] text-[#8B6348] mb-3 font-medium">Get In Touch</p>
        <h1 className="text-5xl md:text-6xl font-bold text-[#2c2420]" style={{ fontFamily: "'Playfair Display', serif" }}>
          Contact Us
        </h1>
        <p className="text-stone-500 mt-4 max-w-lg text-base leading-relaxed">
          Have a question about our furniture, need a quote, or want to schedule a showroom visit? We&apos;re here to help — reach out to the RETAILFLOW LLC team today.
        </p>
      </section>

      {/* Main Content */}
      <section className="w-full bg-[#faf8f5] py-20 px-6 md:px-16">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-16">

          {/* Info Side */}
          <div className="w-full lg:w-[360px] flex-shrink-0 space-y-10">
            <div>
              <h3 className="text-[#2c2420] text-lg font-semibold mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Visit Our Showroom
              </h3>
              <div className="space-y-4">
                {[
                  { icon: 'ri-building-line', text: 'RETAILFLOW LLC' },
                  { icon: 'ri-map-pin-line', text: '4131 W Morten Ave, Phoenix, AZ 85051' },
                  { icon: 'ri-id-card-line', text: 'EIN: 41-3974436' },
                  { icon: 'ri-time-line', text: 'Mon – Fri: 9:00 AM – 6:00 PM\nSat: 10:00 AM – 5:00 PM' },
                  { icon: 'ri-phone-line', text: '+1 (602) 555-0198' },
                  { icon: 'ri-mail-line', text: 'contact@swiftmarketllc.com' },
                ].map((item) => (
                  <div key={item.icon} className="flex gap-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#f0e6d8] flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#8B6348]`} />
                    </div>
                    <p className="text-stone-500 text-sm leading-relaxed whitespace-pre-line">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Map */}
            <div className="w-full h-[240px] rounded-2xl overflow-hidden">
              <iframe
                title="RETAILFLOW LLC - 4131 W Morten Ave, Phoenix, AZ 85051"
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3329.4!2d-112.1318!3d33.5123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872b12345678abcd%3A0x0!2s4131%20W%20Morten%20Ave%2C%20Phoenix%2C%20AZ%2085051!5e0!3m2!1sen!2sus!4v1714000000002"
                className="border-0"
              />
            </div>

            <div>
              <h3 className="text-[#2c2420] text-lg font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Follow Our Story
              </h3>
              <div className="flex gap-3">
                {['ri-instagram-line', 'ri-pinterest-line', 'ri-facebook-line', 'ri-youtube-line'].map((icon) => (
                  <a
                    key={icon}
                    href="#"
                    rel="nofollow"
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-stone-300 text-stone-500 hover:border-[#8B6348] hover:text-[#8B6348] transition-all duration-200 cursor-pointer"
                  >
                    <i className={icon} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Form Side */}
          <div className="flex-1">
            <div className="bg-white rounded-3xl p-8 md:p-12">
              <h3 className="text-[#2c2420] text-2xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Send a Message
              </h3>
              <p className="text-stone-400 text-sm mb-8">We'll get back to you within 24 hours.</p>

              {status === 'success' ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#f0e6d8] rounded-full mx-auto mb-5">
                    <i className="ri-check-line text-2xl text-[#8B6348]" />
                  </div>
                  <h4 className="text-[#2c2420] font-semibold text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Message Sent
                  </h4>
                  <p className="text-stone-400 text-sm">Thank you for reaching out. We'll be in touch shortly.</p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="contact-form"
                  className="space-y-5"
                >
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        placeholder="Isabelle"
                        className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        placeholder="Renard"
                        className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="hello@example.com"
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                      Subject
                    </label>
                    <select
                      name="subject"
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors cursor-pointer"
                    >
                      <option value="Product Enquiry">Product Enquiry</option>
                      <option value="Bespoke Commission">Bespoke Commission</option>
                      <option value="Showroom Visit">Book a Showroom Visit</option>
                      <option value="Delivery & Returns">Delivery &amp; Returns</option>
                      <option value="Press & Collaboration">Press &amp; Collaboration</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us how we can help..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-[#2c2420] bg-[#faf8f5] focus:outline-none focus:border-[#8B6348] transition-colors placeholder-stone-300 resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 450 ? 'text-red-400' : 'text-stone-300'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || charCount > 500}
                    className="whitespace-nowrap w-full py-4 bg-[#2c2420] text-white text-sm font-semibold tracking-wider rounded-xl hover:bg-[#8B6348] transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
